import React, { useEffect, useState } from 'react'
import Search from '../../components/Search'
import Table from '../../components/Table'
import Loader from '../../components/shared/Loader'
import Pagination from '../../components/shared/Pagination'
import useFetch from '../../hooks/useFetch'
import CaptionLayout from '../../layouts/CaptionLayout'
import Section from '../../layouts/Section'
import SectionBody from '../../layouts/SectionBody'
import { cn } from '../../utils/helperFunctions'
import { withErrorHandling } from '../../utils/withErrorHandling'
import { getAdminQuoteCalculations } from '../../utils/Apis/quotes'
import { columns } from './COLUMNS'

const quoteTypeOptions = [
  { value: '', label: 'All Types' },
  { value: 'standard_pcb', label: 'Standard PCB' },
  { value: 'advanced_pcb', label: 'Advanced PCB' },
  { value: 'fpc_rigid_flex', label: 'FPC/Rigid-Flex' },
  { value: 'assembly', label: 'Assembly' },
  { value: 'smd_stencil', label: 'SMD-Stencil' },
  { value: 'cnc_3d', label: 'CNC/3D' },
]

/**
 * 后台报价计算记录列表页面。
 */
const QuoteCalculations = ({ className, ...props }) => {
  const [search, setSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [filters, setFilters] = useState({
    quoteType: '',
    dateFrom: '',
    dateTo: '',
  })

  const { data, error, loading, refetch } = useFetch({
    fn: (params = {}) =>
      getAdminQuoteCalculations({
        keyword: params.keyword ?? search,
        page: params.page ?? currentPage,
        quoteType: params.quoteType ?? filters.quoteType,
        dateFrom: params.dateFrom ?? filters.dateFrom,
        dateTo: params.dateTo ?? filters.dateTo,
      }),
  })

  /**
   * 更新筛选字段。
   * @param {React.ChangeEvent<HTMLInputElement|HTMLSelectElement>} e 输入事件
   */
  const handleFilterChange = (e) => {
    const { name, value } = e.target
    setFilters((prevState) => ({
      ...prevState,
      [name]: value,
    }))
    setCurrentPage(1)
  }

  const handleSearch = withErrorHandling(async (keyword) => {
    setCurrentPage(1)
    refetch({
      keyword,
      page: 1,
    })
  })

  /**
   * 清空筛选条件。
   */
  const clearFilters = () => {
    setSearch('')
    setCurrentPage(1)
    setFilters({
      quoteType: '',
      dateFrom: '',
      dateTo: '',
    })
  }

  useEffect(() => {
    document.title = 'Quote Calculation Records - Admin'
  }, [])

  useEffect(() => {
    refetch({ page: currentPage })
  }, [currentPage, refetch])

  useEffect(() => {
    refetch({ page: 1 })
  }, [filters, refetch])

  useEffect(() => {
    if (search === '') {
      refetch({ keyword: '', page: 1 })
    }
  }, [search, refetch])

  return (
    <div className={className} {...props}>
      <Section title={'Quote Calculation Records'}>
        <Search
          handleSearch={handleSearch}
          search={search}
          setSearch={setSearch}
          className={'top-0'}
          placeholder={'Customer, email, session id'}
        />
      </Section>

      <SectionBody>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <select
            name="quoteType"
            value={filters.quoteType}
            onChange={handleFilterChange}
            className="rounded-md border border-primary/20 p-2"
          >
            {quoteTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <input
            type="date"
            name="dateFrom"
            value={filters.dateFrom}
            onChange={handleFilterChange}
            className="rounded-md border border-primary/20 p-2"
          />
          <input
            type="date"
            name="dateTo"
            value={filters.dateTo}
            onChange={handleFilterChange}
            className="rounded-md border border-primary/20 p-2"
          />
          <button
            type="button"
            onClick={clearFilters}
            className="rounded-md border border-primary/20 px-3 py-2 text-sm"
          >
            Clear
          </button>
        </div>

        {loading ? (
          <Loader />
        ) : error ? (
          <p>Error</p>
        ) : (
          <>
            <CaptionLayout results={data?.totalCount} caption={'Calculation records'}>
              <Pagination
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                pages={data?.paginationResult?.numberOfPages}
              />
            </CaptionLayout>

            <div
              className={cn(
                'custom-scroll relative max-h-[45vh] w-full overflow-auto md:max-h-[60vh]',
                className,
              )}
            >
              <Table data={data?.data || []} columns={columns} />
              <div className="pointer-events-none sticky bottom-0 left-0 h-16 w-full bg-gradient-to-t from-background to-transparent" />
            </div>
            <Pagination
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              pages={data?.paginationResult?.numberOfPages}
            />
          </>
        )}
      </SectionBody>
    </div>
  )
}

export default QuoteCalculations
